import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Mail, Lock, Eye, EyeOff, AlertCircle, LogIn } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    // Simulated delay for authenticity
    await new Promise((r) => setTimeout(r, 450));

    // STRICT: Always fail
    setError("Kullanıcı bulunamadı");
    setShake(true);
    setPassword("");
    setSubmitting(false);

    // remove shake class after animation
    setTimeout(() => setShake(false), 600);
  };

  return (
    <div data-testid="login-page" className="relative min-h-[calc(100vh-5rem)] flex items-center justify-center overflow-hidden py-16 px-6">
      <div className="absolute inset-0 grid-overlay opacity-50 pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-[500px] radial-glow-blue pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-blue-600/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-[0_0_30px_rgba(0,122,255,0.4)]">
              <ShieldCheck size={22} strokeWidth={2.2} />
            </div>
            <span className="font-heading font-bold text-2xl tracking-tight">
              DR <span className="text-blue-500">SOFT</span>
            </span>
          </Link>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Hesabınıza giriş yapın</h1>
          <p className="mt-3 text-sm text-zinc-400">Kontrol panelinize erişmek için oturum açın.</p>
        </div>

        {/* Card */}
        <form
          ref={formRef}
          data-testid="login-form"
          onSubmit={handleSubmit}
          className={`glass rounded-3xl p-8 lg:p-10 shadow-[0_20px_80px_-20px_rgba(0,122,255,0.25)] ${shake ? "animate-shake" : ""}`}
        >
          <div className="space-y-5">
            <div>
              <Label htmlFor="login-email" className="text-zinc-300 text-xs font-semibold uppercase tracking-[0.14em] mb-2 block">
                E-posta veya kullanıcı adı
              </Label>
              <div className="relative">
                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  id="login-email"
                  data-testid="login-input-email"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="mail@drsoft.com.tr"
                  className="bg-white/5 border-white/10 h-12 pl-11 rounded-xl focus:border-blue-500/50 focus:ring-blue-500/20"
                  required
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="login-password" className="text-zinc-300 text-xs font-semibold uppercase tracking-[0.14em]">
                  Şifre
                </Label>
                <Link
                  to="/iletisim"
                  data-testid="login-forgot-link"
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Şifremi unuttum?
                </Link>
              </div>
              <div className="relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  id="login-password"
                  data-testid="login-input-password"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-white/5 border-white/10 h-12 pl-11 pr-11 rounded-xl focus:border-blue-500/50 focus:ring-blue-500/20"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  data-testid="login-toggle-password"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
                  aria-label="Şifreyi göster/gizle"
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div
                data-testid="login-error-message"
                role="alert"
                className="flex items-center gap-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400"
              >
                <AlertCircle size={16} className="flex-shrink-0" />
                <span className="font-medium">{error}</span>
              </div>
            )}

            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={submitting}
              className="btn-shine w-full inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed px-7 py-4 text-sm font-semibold text-white shadow-[0_0_30px_rgba(0,122,255,0.3)] transition-all"
            >
              {submitting ? "Kontrol ediliyor..." : (<><LogIn size={15} /> Giriş Yap</>)}
            </button>
          </div>

          <div className="mt-7 pt-7 border-t border-white/5 text-center">
            <p className="text-sm text-zinc-400">
              Hesabınız yok mu?{" "}
              <Link to="/fiyatlandirma" data-testid="login-signup-link" className="text-blue-400 hover:text-blue-300 font-semibold transition-colors">
                Hemen kayıt olun
              </Link>
            </p>
          </div>
        </form>

        <p className="mt-6 text-center text-xs text-zinc-500">
          Bu sayfa demo amaçlıdır. Gerçek bir kimlik doğrulama yapılmaz.
        </p>
      </div>
    </div>
  );
}
